<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <div>
      <v-row class="ma-0 mb-3">
        <h2>{{ title }}</h2>
        <v-btn
          class="ml-2"
          v-if="!useForSelection"
          color="primary"
          v-on:click="createNewPerson"
          >+ New Person</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn color="primary" v-on:click="applyFilter">Apply Filters</v-btn
        >&nbsp;
        <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
      </v-row>
    </div>
    <PaginationSelect
      :totalCount="paginatedResponse.totalCount"
      :page="options.page"
      :size="options.itemsPerPage"
      v-on:load="load"
      v-on:changePage="options.page = $event"
      v-on:changeSize="options.itemsPerPage = $event"
    />
    <v-row>
      <v-alert
        width="100%"
        class="mr-5 ml-5"
        v-model="isError"
        dismissible
        type="error"
        >{{ errorMessage }}</v-alert
      >
    </v-row>
    <v-data-table
      dense
      disable-sort
      single-select
      hide-default-footer
      class="elevation-1"
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
      :show-select="useForSelection"
    >
      <template v-slot:item.data-table-select="{ item }">
        <v-btn
          v-if="useForSelection"
          class="mt-2"
          icon
          v-on:click="() => onRowSelected(item)"
          color="primary"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
      <template v-slot:header.name="{ header }">
        <div>
          <v-text-field
            v-model="searchParams.name"
            label="Name"
            clearable
            @click:clear="searchParams.name = ''"
            v-on:keyup.enter="applyFilter"
          />
          <v-text-field
            hide-details
            class="mt-0 pt-0 mb-2"
            v-model="searchParams.username"
            label="Username"
            clearable
            @click:clear="searchParams.username = ''"
            v-on:keyup.enter="applyFilter"
          />
        </div>
      </template>
      <template v-slot:header.memberId="{ header }">
        <div>
          <v-text-field
            v-model="searchParams.memberId"
            label="Member ID"
            clearable
            @click:clear="searchParams.memberId = ''"
            v-on:keyup.enter="applyFilter"
          />
          <v-text-field
            hide-details
            class="mt-0 pt-0 mb-2"
            v-model="searchParams.judgeId"
            label="Judge ID"
            clearable
            @click:clear="searchParams.judgeId = ''"
            v-on:keyup.enter="applyFilter"
          />
        </div>
      </template>
      <template v-slot:header.email="{ header }">
        <div>
          <v-text-field
            v-model="searchParams.email"
            label="Email"
            clearable
            @click:clear="searchParams.email = ''"
            v-on:keyup.enter="applyFilter"
          />
          <v-text-field
            v-model="searchParams.phone"
            label="Phone"
            clearable
            @click:clear="searchParams.phone = ''"
            v-on:keyup.enter="applyFilter"
          />
        </div>
      </template>

      <template v-slot:header.affiliationType="{ header }">
        <div>
          Affiliation Type
          <filter-pills :pills="affiliationFilters" :toggle="changeFilters" />
        </div>
      </template>
      <template v-slot:header.memberType="{ header }">
        <v-select
          v-model="searchParams.membershipType"
          label="Member Type"
          :items="memberTypes"
          item-text="name"
          item-value="value"
          width="80px"
        ></v-select
        ><br />
        <v-checkbox
          :indeterminate="searchParams.membershipStatus == null"
          v-model="searchParams.membershipStatus"
          label="Active Member"
          v-on:change="toggleCheckbox"
        />
      </template>
      <template v-slot:header.formattedAddress="{ header }">
        <div>
          <v-text-field
            v-model="searchParams.address"
            label="Address"
            clearable
            @click:clear="searchParams.address = ''"
            v-on:keyup.enter="applyFilter"
          />
        </div><br />
        <v-checkbox
          :indeterminate="searchParams.isAdminUser == null"
          v-model="searchParams.isAdminUser"
          label="Admin"
          v-on:change="toggleisAdminUserCheckbox"
        />
      </template>
      <template v-slot:item.memberId="{ item }">
        <div>
          {{ item.memberId }}
          <span v-if="item.judgeId"
            ><br />
            - {{ item.judgeId }}</span
          >
        </div>
      </template>
      <template v-slot:item.email="{ item }">
        <div>
          <a :href="`mailto:${item.email}`">{{ item.email }}</a
          ><br />
          <span>{{ item.phone }}</span>
        </div>
      </template>
      <template v-slot:item.membershipType="{ item }">
        <div>
          {{ item.membershipType }} <br />
          <a :href="`tel:${item.phone}`">{{ item.phone }}</a>
        </div>
      </template>
      <template v-slot:item.name="{ item }">
        <router-link :to="`/admin/people/${item.personId}`" target="_blank">
          {{ item.name }}
        </router-link>
        <span v-if="item.username"><br />{{ item.username }}</span>
      </template>
      <template v-slot:item.affiliationType="{ item }">
        <span>
          <v-chip class="white--text ma-1" v-if="item.isMember" color="red"
            >Member</v-chip
          >
          <v-chip
            class="white--text ma-1"
            v-if="item.isAffiliateOfficer"
            color="orange"
            >Affiliate Officer</v-chip
          >
          <v-chip class="ma-1" v-if="item.isJuniorHandler" color="yellow"
            >Jr Handler</v-chip
          >
          <v-chip
            class="white--text ma-1"
            v-if="item.isNationalDirector"
            color="green"
            >National Director</v-chip
          >
          <v-chip
            class="white--text ma-1"
            v-if="item.isNationalOfficer"
            color="blue"
            >National Officer</v-chip
          >
          <v-chip class="white--text ma-1" v-if="item.isJudge" color="purple"
            >Judge</v-chip
          >
        </span>
      </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import FilterPills from "@/components/Layouts/FilterPills.vue";
import PaginationSelect from "@/components/common/PaginationSelect.vue";

import peopleService from "@/services/admin/peopleService";
import { getErrorMessage } from "@/services/errorHandler";

import { MetaNames } from "@/constants";
import { UserInfo } from "@/types/User";
import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";

export default Vue.extend({
  name: "PeopleList",
  components: {
    FilterPills,
    PaginationSelect
  },
  props: {
    useForSelection: { type: Boolean },
    onRowSelected: { type: Function },
  },
  data: function () {
    return {
      title: MetaNames.AdminPeople,
      loading: false,
      isError: false,
      errorMessage: "",
      paginatedResponse: {} as PaginatedResponse<object>,
      searchParams: {
        name: "",
        memberId: "",
        address: "",
        isMember: null,
        isAffiliateOfficer: null,
        isNationalOfficer: null,
        isNationalDirector: null,
        isJudge: null,
        isJuniorHandler: null,
        membershipType: null,
        membershipStatus: null as any,
        judgeId: "",
        phone: "",
        email: "",
        username: "",
        isAdminUser: null as any,
      },
      options: {} as PaginationOptions,
      headers: [
        { text: "", align: "left", value: "name", width: "20%" },
        { text: "", align: "left", value: "email", width: "20%" },
        { text: "", align: "left", value: "memberId", width: "10%" },
        { text: "", align: "left", value: "affiliationType", width: "15%" },
        { text: "", align: "left", value: "memberType", width: "10%" },
        { text: "", align: "left", value: "formattedAddress", width: "25%" },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50, 100] },
      memberTypes: [
        { name: "Single", value: 1 },
        { name: "Dual", value: 2 },
        { name: "Junior", value: 4 },
        { name: "Service", value: 5 },
        { name: "Lifetime", value: 6 },
        { name: "Lifetime Dual", value: 7 },
      ],
      currentCheckboxIdx: 0,
      currentisAdminUserCheckboxIdx: 0,
    };
  },
  created: function () {
    this.load();
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    }
  },
  methods: {
    createNewPerson: function () {
      this.$router.push({ path: "/admin/people" });
    },
    clearAllFilters: function () {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.searchParams = {
        name: "",
        address: "",
        memberId: "",
        isMember: null,
        isAffiliateOfficer: null,
        isNationalOfficer: null,
        isNationalDirector: null,
        isJudge: null,
        isJuniorHandler: null,
        membershipType: null,
        membershipStatus: null,
        judgeId: "",
        phone: "",
        email: "",
        username: "",
        isAdminUser: null,
      };
    },
    changeFilters: function (index: number) {
      (this as any).searchParams[this.affiliationFilters[index].var] = !(
        this as any
      ).searchParams[this.affiliationFilters[index].var];
    },
    toggleCheckbox: function () {
      var states = [null, true, false];
      var next = ++this.currentCheckboxIdx % states.length;
      this.currentCheckboxIdx = next;
      this.searchParams.membershipStatus = states[next];
    },
    toggleisAdminUserCheckbox: function () {
      var states = [null, true, false];
      var next = ++this.currentisAdminUserCheckboxIdx % states.length;
      this.currentCheckboxIdx = next;
      this.searchParams.isAdminUser = states[next];
    },
    load: async function () {
      this.loading = true;

      const {
        page,
        itemsPerPage,
      } = this.options;

      try {
        const response = await peopleService.getPeopleList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;
        this.isError = true;

        this.errorMessage = getErrorMessage(ex);
      }
    },
    applyFilter() {
      this.options.page = 1;
      this.options.itemsPerPage = 10;

      this.load();
    }
  },
  computed: {
    affiliationFilters(): any {
      return [
        {
          name: "Member",
          var: "isMember",
          active: this.$data.searchParams.isMember,
        },
        {
          name: "Affiliate Officer",
          var: "isAffiliateOfficer",
          active: this.$data.searchParams.isAffiliateOfficer,
        },
        {
          name: "National Officer",
          var: "isNationalOfficer",
          active: this.$data.searchParams.isNationalOfficer,
        },
        {
          name: "National Director",
          var: "isNationalDirector",
          active: this.$data.searchParams.isNationalDirector,
        },
        {
          name: "Judge",
          var: "isJudge",
          active: this.$data.searchParams.isJudge,
        },
        {
          name: "Junior Handler",
          var: "isJuniorHandler",
          active: this.$data.searchParams.isJuniorHandler,
        },
      ];
    },
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
  },
});
</script>
